import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Search, MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { Helmet } from 'react-helmet-async';

function FAQItem({ q, a, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/5 last:border-0">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between py-3.5 text-left gap-3"
        data-testid={testId}
      >
        <span className="text-[#e8e8e8] text-sm font-medium">{q}</span>
        {open ? <ChevronUp size={14} className="text-[#ff6b6b] flex-shrink-0" /> : <ChevronDown size={14} className="text-[#a0a0ab] flex-shrink-0" />}
      </button>
      {open && <p className="text-[#a0a0ab] text-sm pb-3.5 leading-relaxed">{a}</p>}
    </div>
  );
}

export default function FAQ() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const categories = t('faq.categories', { returnObjects: true }) || [];

  const filtered = categories.map(cat => ({
    ...cat,
    items: cat.items.filter(i =>
      i.q.toLowerCase().includes(search.toLowerCase()) ||
      i.a.toLowerCase().includes(search.toLowerCase())
    ),
  })).filter(cat => cat.items.length > 0);

  return (
    <Layout>
      <Helmet>
        <title>{t('faq.metaTitle')}</title>
        <meta name="description" content={t('faq.metaDesc')} />
      </Helmet>

      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-heading font-bold text-white mb-3">{t('faq.title')}</h1>
          <p className="text-[#a0a0ab]">{t('faq.subtitle')}</p>
        </div>

        <div className="relative mb-8">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#a0a0ab]" />
          <input
            type="text" placeholder={t('faq.searchPlaceholder')}
            value={search} onChange={e => setSearch(e.target.value)}
            data-testid="faq-search"
            className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white text-sm focus:outline-none focus:border-[#ff6b6b]/50"
          />
        </div>

        <div className="space-y-6">
          {filtered.map((cat, ci) => (
            <div key={cat.category} className="kurso-card p-5" data-testid={`faq-category-${ci}`}>
              <h2 className="text-[#ff6b6b] font-semibold text-sm uppercase tracking-wide mb-3">{cat.category}</h2>
              {cat.items.map((item, i) => <FAQItem key={item.q} q={item.q} a={item.a} testId={`faq-item-${ci}-${i}`} />)}
            </div>
          ))}
          {filtered.length === 0 && (
            <p className="text-center text-[#a0a0ab] py-8" data-testid="faq-no-result">{t('faq.noResult', { search })}</p>
          )}
        </div>

        <div className="mt-10 kurso-card p-6 text-center" data-testid="faq-contact-cta">
          <MessageSquare size={24} className="text-[#ff6b6b] mx-auto mb-3" />
          <h3 className="text-white font-bold mb-2">{t('faq.ctaTitle')}</h3>
          <p className="text-[#a0a0ab] text-sm mb-4">{t('faq.ctaDesc')}</p>
          <a href="/contact" className="inline-block bg-[#ff6b6b] text-white px-6 py-2.5 rounded-xl font-semibold text-sm hover:bg-[#ff5555] transition-colors">
            {t('faq.ctaBtn')}
          </a>
        </div>
      </div>
    </Layout>
  );
}
